import { Box, Button, IconButton, Paper, Stack, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "~/auth/AuthProvider";
import Config from "~/config";
import logger from "~/helpers/logger";
import fetchAvailableVersion from "~/helpers/sw/fetchAvailableVersion";
import whatsNewReload from "~/pages/home/helpers/whatsNewReload";
import WhatsNewContext from "~/pages/home/WhatsNewContext";

import { useRegisterSW } from "virtual:pwa-register/react";

const swCheckInterval = 5 * 60 * 1000;
const reloadFallbackMs = 2000;

// Each release's notes deploy overwrites these paths in place, so the outgoing bundle still links to the newest publish.
const latestGeneralReleaseNotesUrl = "https://worksuite-release-notes.netlify.app";
const latestBonnieReleaseNotesUrl = "https://worksuite-release-notes.netlify.app/bonnie/";

// Verbatim JWT wsTenantId claim value; must match wsapi WorkSuite.Domain/Shared/TenantIds.Bpf
const bpfTenantId = "BPF";

function ReloadPrompt() {
	const { t } = useTranslation("common");
	const { wsapiTenant } = useContext(AuthContext);
	const whatsNew = useContext(WhatsNewContext);
	const showsInAppNotes = wsapiTenant !== bpfTenantId && Boolean(whatsNew?.visible);
	const [availableVersion, setAvailableVersion] = useState<string | null>(null);
	const reloadTriggered = useRef(false);

	const releaseNotesUrl = wsapiTenant === bpfTenantId ? latestBonnieReleaseNotesUrl : latestGeneralReleaseNotesUrl;

	const {
		needRefresh: [needRefresh, setNeedRefresh],
		updateServiceWorker
	} = useRegisterSW({
		onRegistered(r) {
			logger.log("SW Registered: " + r);
			if (r) {
				setInterval(() => {
					if (!navigator.onLine) return;
					r.update().catch(() => {});
				}, swCheckInterval);
			}
		},
		onRegisterError(error) {
			logger.error("SW registration error", error);
		}
	});

	useEffect(() => {
		if (!needRefresh) {
			setAvailableVersion(null);
			return;
		}
		fetchAvailableVersion().then(setAvailableVersion);
	}, [needRefresh]);

	const handleReload = () => {
		if (reloadTriggered.current) return;
		reloadTriggered.current = true;
		updateServiceWorker(true);
		window.setTimeout(() => {
			window.location.reload();
		}, reloadFallbackMs);
	};

	const close = () => {
		setNeedRefresh(false);
	};

	if (!needRefresh) return null;
	const showAvailable = availableVersion && availableVersion !== Config.buildVersion;

	return (
		<>
			<Box
				sx={{
					bottom: 24,
					left: { sm: 24, xs: 16 },
					position: "fixed",
					right: { sm: "auto", xs: 16 },
					width: { sm: 420, xs: "auto" },
					zIndex: 3000
				}}
			>
				<Paper
					className="ReloadPrompt-toast"
					elevation={6}
					sx={{
						backgroundColor: "background.paper",
						borderColor: "primary.main",
						borderRadius: 2,
						borderStyle: "solid",
						borderWidth: 1,
						color: "text.primary",
						overflow: "hidden",
						p: 2.5
					}}
				>
					<Stack direction="row" spacing={1.75}>
						<Icon color="primary" sx={{ fontSize: 32, mt: "2px" }}>
							new_releases
						</Icon>
						<Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
							<Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
								<Stack spacing={0.5} sx={{ flex: 1 }}>
									<Typography sx={{ color: "text.primary", fontWeight: 600 }} variant="subtitle1">
										{t("shell.update.title")}
									</Typography>
									<Typography sx={{ color: "text.primary" }} variant="body2">
										{t("shell.update.body")}
									</Typography>
									<Typography sx={{ color: "text.secondary" }} variant="caption">
										{t("shell.update.currentLabel")}{" "}
										<strong>{Config.buildVersion || t("shell.update.unknownVersion")}</strong>
										{showAvailable && (
											<>
												{" "}
												• {t("shell.update.updateLabel")} <strong>{availableVersion}</strong>
											</>
										)}
									</Typography>
								</Stack>
								<IconButton
									aria-label={t("actions.dismiss")}
									onClick={close}
									size="small"
									sx={{ color: "text.secondary", mr: -0.5, mt: -0.5 }}
								>
									<Icon fontSize="small">close</Icon>
								</IconButton>
							</Stack>
							<Stack
								direction="row"
								spacing={0.75}
								sx={{ alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", rowGap: 0.5 }}
							>
								{showsInAppNotes ? (
									<Button
										color="primary"
										onClick={() => {
											whatsNewReload.mark();
											handleReload();
										}}
										size="small"
										sx={{ textTransform: "none" }}
									>
										{t("shell.update.whatsNew")}
									</Button>
								) : (
									<Button
										color="primary"
										href={releaseNotesUrl}
										rel="noopener noreferrer"
										size="small"
										sx={{ textTransform: "none" }}
										target="_blank"
									>
										{t("shell.update.whatsNew")}
									</Button>
								)}
								<Button
									color="primary"
									onClick={handleReload}
									size="small"
									sx={{ textTransform: "none" }}
									variant="contained"
								>
									{t("actions.reload")}
								</Button>
							</Stack>
						</Stack>
					</Stack>
				</Paper>
			</Box>
		</>
	);
}

export default ReloadPrompt;
